import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Instagram } from "lucide-react";

import { content } from "@/i18n/content";
import { useLang } from "@/i18n/language";
import { Section } from "./Primitives";

/**
 * Permalinks de publicaciones de Instagram a mostrar (formato:
 * "https://www.instagram.com/p/XXXXXXXXXXX/").
 */
const POSTS: string[] = [
  "https://www.instagram.com/p/DcEjsbCltLV/",
  "https://www.instagram.com/p/Db6MlvUFtRF/",
  "https://www.instagram.com/p/Dai6rCfjptp/",
  "https://www.instagram.com/p/DZbDQzTiHgh/",
  "https://www.instagram.com/p/DYP3e63FmF1/",
];

function normalizePostUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const match = parsed.pathname.match(/\/p\/[^/]+/);
    return match ? `https://www.instagram.com${match[0]}/` : url;
  } catch {
    return url;
  }
}

export function InstagramFeed() {
  const { lang } = useLang();
  const t = content[lang].instagram;
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    if (!card) return;
    const step = card.offsetWidth + 24;
    setActive(Math.round(el.scrollLeft / step));
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const go = (dir: -1 | 1) => {
    const el = scroller.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    if (!card) return;
    el.scrollBy({ left: dir * (card.offsetWidth + 24), behavior: "smooth" });
  };

  return (
    <Section id="instagram" tone="surface">
      <div className="grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center">
        <div className="min-w-0">
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-primary/70" aria-hidden="true" />
            <p className="eyebrow text-primary">{t.eyebrow}</p>
          </div>
          <h2 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight md:text-5xl">
            {t.titleTop} <em className="italic text-primary/90">{t.titleAccent}</em>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">{t.lead}</p>
          <a
            href={t.profileUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
            style={{ boxShadow: "var(--shadow-amber)" }}
          >
            <Instagram className="size-4" />
            {t.cta}
          </a>
          <p className="mt-4 eyebrow text-muted-foreground">{t.handle}</p>
        </div>

        <div className="min-w-0">
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Anterior"
              className="grid size-11 place-items-center rounded-full border border-foreground/15 text-foreground/70 transition-colors hover:border-primary hover:text-primary"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Siguiente"
              className="grid size-11 place-items-center rounded-full border border-foreground/15 text-foreground/70 transition-colors hover:border-primary hover:text-primary"
            >
              <ChevronRight className="size-5" />
            </button>
          </div>

          <div
            ref={scroller}
            className="mt-6 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {POSTS.map((url) => {
              const normalized = normalizePostUrl(url);
              return (
                <article
                  key={normalized}
                  className="group relative w-[80%] shrink-0 snap-start overflow-hidden rounded-3xl border border-foreground/10 bg-background sm:w-[55%] lg:w-[75%]"
                  style={{ boxShadow: "var(--shadow-lift)" }}
                >
                  <iframe
                    src={`${normalized}embed`}
                    title={`Instagram ${normalized}`}
                    loading="lazy"
                    scrolling="no"
                    allowTransparency
                    className="h-[620px] w-full border-0"
                  />
                </article>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-center gap-2" aria-hidden="true">
            {POSTS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === active ? "w-6 bg-primary" : "w-1.5 bg-foreground/25"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}
